/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Button, Typography, Alert } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import useStyles from "styles/pack/slots";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { SimpleTextInput } from "components/FormTextInput";
import classNames from "classnames";
// import { useSnackbar } from "hooks/useSnackbar";
import SearchableInput from "components/SearchableInput";
import debounce from "lodash.debounce";
import { useLocation } from "react-router-dom";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

const defaultPageSize = 10,
  defaultPageNo = 1;

const buildLabel = (player: any) =>
  `${player?.fullName} - ${player?.nationality} - ${player?.club}`;

const AdditionalDataViewer = ({ data, label, id }: any) => {
  const styles = useStyles();

  // Special handling for isDisabled field to show status icons
  const renderValue = () => {
    if (id === "isDisabled") {
      const isDisabled = data?.[id];
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {isDisabled ? (
            <>
              <CancelIcon sx={{ color: "red", fontSize: 24 }} />
              <Typography sx={{ color: "red", fontWeight: 500 }}>
                Disabled
              </Typography>
            </>
          ) : (
            <>
              <CheckCircleIcon sx={{ color: "green", fontSize: 24 }} />
              <Typography sx={{ color: "green", fontWeight: 500 }}>
                Enabled
              </Typography>
            </>
          )}
        </Box>
      );
    }
    return (
      <Typography className={styles.additionDetailsText}>
        {data?.[id]}
      </Typography>
    );
  };

  return (
    <Box style={{ display: "column" }}>
      <Typography className={styles.additionDetailsLabel}>{label}</Typography>
      <Box className={styles.additionalDataContainer}>{renderValue()}</Box>
    </Box>
  );
};

const AddAndDelete = ({ add, remove, isHidden }: any) => {
  const styles = useStyles();

  return (
    <Box
      className={classNames(styles.addRemoveContainer, {
        [styles.hide]: isHidden,
      })}
    >
      <Button variant="contained" onClick={add}>
        Add Row
      </Button>
      <Button variant="contained" onClick={remove}>
        Remove All
      </Button>
    </Box>
  );
};

const PlayerSlot = ({
  slotData,
  slotIndex,
  handleChange = (e?: any, slotIdx?: any, dropIdx?: any) => {},
  removeOne = (slotIdx?: any, dropIdx?: any) => {},
  validationError,
}) => {
  const styles = useStyles();

  // Get validation error for specific dropProbability
  const getDropProbabilityError = (dropIndex) => {
    if (!validationError?.dropProbabilityResults) return null;
    return validationError.dropProbabilityResults.find(
      (result) => result.dropProbabilityIndex === dropIndex && !result.isValid
    );
  };

  return (
    <>
      {validationError && (
        <Alert
          severity="error"
          icon={<ErrorOutlineIcon />}
          sx={{ mb: 2, width: "100%" }}
        >
          <Typography variant="body2" fontWeight="bold">
            Slot {slotIndex + 1} Validation Error
          </Typography>
          <Typography variant="body2">{validationError.summary}</Typography>
        </Alert>
      )}

      {slotData?.dropProbabilities.map((dropProb, dropIndex) => {
        const dropError = getDropProbabilityError(dropIndex);

        return (
          <Box key={dropIndex}>
            <Box className={styles.dropProbabilities}>
              <Box className={styles.smallField}>
                <SimpleTextInput
                  name="ovrMin"
                  label="OVR Min"
                  value={dropProb?.ovrMin}
                  onChange={(e) => handleChange(e, slotIndex, dropIndex)}
                  error={!!dropError}
                />
              </Box>
              <Box className={styles.smallField}>
                <SimpleTextInput
                  name="ovrMax"
                  label="OVR Max"
                  value={dropProb?.ovrMax}
                  onChange={(e) => handleChange(e, slotIndex, dropIndex)}
                  error={!!dropError}
                />
              </Box>
              <Box className={styles.smallField}>
                <SimpleTextInput
                  name="probability"
                  label="Probability (%)"
                  value={dropProb?.probability}
                  type="number"
                  onChange={(e) => handleChange(e, slotIndex, dropIndex)}
                />
              </Box>
              {dropIndex !== 0 && (
                <DeleteIcon
                  style={{
                    marginTop: "30px",
                    color: "red",
                    marginLeft: "10px",
                    cursor: "pointer",
                  }}
                  onClick={() => removeOne(slotIndex, dropIndex)}
                />
              )}
            </Box>

            {dropError && (
              <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
                <Typography variant="body2" fontWeight="bold">
                  OVR Range {dropError.ovrMin}-{dropError.ovrMax}:{" "}
                  {dropError.playerCount} players available
                </Typography>
                {dropError.playerPosition?.length > 0 && (
                  <Typography variant="caption" display="block">
                    Positions: {dropError.playerPosition.join(", ")}
                  </Typography>
                )}
                {dropError.tags?.length > 0 && (
                  <Typography variant="caption" display="block">
                    Tags: {dropError.tags.join(", ")}
                  </Typography>
                )}
                <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                  {dropError.errorMessage}
                </Typography>
              </Alert>
            )}
          </Box>
        );
      })}
    </>
  );
};

const NamedPlayerSlot = ({
  slotData,
  slotIndex,
  handleChange,
  playerCards,
  playerCardOptions,
  fetchPlayerCards,
}) => {
  const styles = useStyles();
  const [cardDetails, setCardDetails] = useState(null);
  const [pagination] = useState({
    pageSize: defaultPageSize,
    pageNo: defaultPageNo,
  });
  const addiotionDataToShow = [
    {
      id: "fullName",
      label: "Full Name",
    },
    {
      id: "rarity",
      label: "Rarity",
    },
    {
      id: "overallRating",
      label: "OVR",
    },
    {
      id: "club",
      label: "Club",
    },
    {
      id: "preferredPosition",
      label: "Position",
    },
    {
      id: "isDisabled",
      label: "Status",
    },
  ];

  const getPlayerCardCallback = useCallback(
    async (filters) => {
      fetchPlayerCards(filters);
    },
    [fetchPlayerCards]
  );

  const onSearchChange = async (searchValue) => {
    if (searchValue !== "") {
      getPlayerCardCallback({
        ...pagination,
        search: searchValue,
        skipPagination: true,
      });
    } else {
      fetchPlayerCards({ ...pagination }, true);
    }
  };

  const optimizeSearch = debounce(onSearchChange, 300);

  const onChange = (e: any) => {
    setCardDetails(
      playerCards.find((item) => item?._id === e?.target?.value?.id)
    );
    handleChange(e, slotIndex);
  };

  useEffect(() => {
    const playerCard = playerCards.find(
      (item) => item?._id === slotData?.playerCard?.id
    );

    setCardDetails(playerCard);
  }, [slotData?.playerCard?.id]);

  return (
    <Box
      style={{
        display: "flex",
        flexFlow: "row wrap",
        marginLeft: 8,
        gap: 5,
        alignItems: "flex-end",
      }}
    >
      <SearchableInput
        label={"Player Card"}
        name={"playerCard"}
        selected={slotData?.playerCard?.id ? slotData.playerCard : null}
        options={playerCardOptions}
        onInputChange={optimizeSearch}
        onOptionSelect={onChange}
        placeholder="Select a player"
        customStyle={{ width: "220px" }}
      />
      <Box
        className={classNames(styles.namedPlayerDetailsContainer, {
          [styles.hide]: cardDetails == null,
        })}
      >
        {cardDetails &&
          addiotionDataToShow.map((_inputs, index) => {
            return (
              <AdditionalDataViewer
                key={index}
                data={cardDetails}
                {..._inputs}
              />
            );
          })}
      </Box>
    </Box>
  );
};

const BoosterSlot = ({
  slotData,
  slotIndex,
  options,
  handleChange = (e?: any, slotIdx?: any, boosterIdx?: any) => {},
  removeOne = (slotIdx?: any, boosterIdx?: any) => {},
  onSearchChange = (searchValue?: any, isFetchMore?: any) => {},
}) => {
  const styles = useStyles();
  const location = useLocation();
  const id = location.pathname.split("/").slice(-1)[0];
  return (
    <Box className={styles.dropProbabilities}>
      {slotData?.boosterProbabilities?.map((booster, boosterIndex) => {
        let selected;
        if (id !== "create")
          selected = options.find((bp) => bp.id === booster?.boosterId);
        else selected = booster?.boosterId;
        return (
          <Box key={boosterIndex} className={styles.dropProbabilities}>
            <Box style={{ display: "flex", gap: 5 }}>
              <SearchableInput
                label={"Boosters"}
                name={"boosterId"}
                options={options || []}
                selected={selected}
                onInputChange={onSearchChange}
                onOptionSelect={(e) => handleChange(e, slotIndex, boosterIndex)}
                placeholder="Select a boost"
                customStyle={{
                  width: "220px",
                }}
              />

              <SimpleTextInput
                label={"Boost Probability (%)"}
                name={"probability"}
                value={booster?.probability}
                onChange={(e) => handleChange(e, slotIndex, boosterIndex)}
              />
              {boosterIndex !== 0 && (
                <DeleteIcon
                  style={{
                    marginTop: "30px",
                    color: "red",
                    marginLeft: "10px",
                    cursor: "pointer",
                  }}
                  onClick={() => removeOne(slotIndex, boosterIndex)}
                />
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

const PlayerPoolSlot = ({
  slotData,
  slotIndex,
  handleChange,
  playerCards,
  fetchPlayerCards,
  removeOne = (slotIdx?: any, ppIdx?: any) => {},
}) => {
  const styles = useStyles();
  // const [loading, setLoading] = useState(false);
  // const { showSnackbar } = useSnackbar();
  const [pagination] = useState({
    pageSize: defaultPageSize,
    pageNo: defaultPageNo,
  });

  const getPlayerCardCallback = useCallback(
    async (filters) => {
      fetchPlayerCards(filters);
    },
    [fetchPlayerCards]
  );

  const onSearchChange = async (searchValue) => {
    if (searchValue !== "") {
      getPlayerCardCallback({
        ...pagination,
        search: searchValue,
        skipPagination: true,
      });
    } else {
      fetchPlayerCards(null, "default");
    }
  };

  const optimizeSearch = debounce(onSearchChange, 400);

  return (
    <Box>
      {slotData?.playerPool?.map((playerPool, ppIndex) => {
        return (
          <Box
            key={playerPool.playerCard?.id}
            style={{
              display: "flex",
              flexFlow: "row wrap",
              gap: 5,
              marginLeft: 8,
            }}
          >
            <SearchableInput
              selected={playerPool?.playerCard}
              label={"Select Player Card"}
              name={"playerCard"}
              options={playerCards}
              onInputChange={optimizeSearch}
              onOptionSelect={(e) => handleChange(e, slotIndex, ppIndex)}
              placeholder="Select Player card"
              // loading={loading}
              customStyle={{
                width: "220px",
              }}
            />
            <Box className={styles.smallField}>
              <SimpleTextInput
                label={"Player Probability (%)"}
                name={"probability"}
                value={playerPool?.probability}
                onChange={(e) => handleChange(e, slotIndex, ppIndex)}
              />
            </Box>
            {ppIndex !== 0 && (
              <DeleteIcon
                style={{
                  marginTop: "30px",
                  color: "red",
                  marginLeft: "10px",
                  cursor: "pointer",
                }}
                onClick={() => removeOne(slotIndex, ppIndex)}
              />
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export {
  buildLabel,
  AddAndDelete,
  PlayerSlot,
  NamedPlayerSlot,
  BoosterSlot,
  PlayerPoolSlot,
};
